// FieldVision Admin - User List Component v1.0
// Viser liste over brugere med soegning, kvota og status

import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, createUser, User } from '../services/api';

// Tier konfiguration
const TIER_CONFIG: Record<string, { color: string; bgColor: string }> = {
  Basis: { color: '#6b7280', bgColor: '#f3f4f6' },
  Pro: { color: '#2563eb', bgColor: '#dbeafe' },
  Advanced: { color: '#9333ea', bgColor: '#f3e8ff' },
};

interface UserListProps {
  onSelectUser: (userId: number) => void;
}

const UserList: React.FC<UserListProps> = ({ onSelectUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ Email: '', Name: '', Phone: '', CVR: '', CompanyName: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getUsers(searchDebounce || undefined);
      setUsers(data.users);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchDebounce]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async () => {
    if (!createForm.Email || !createForm.Name) {
      setCreateError('Email og Navn er påkrævet');
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      const result = await createUser(createForm);
      setCreatedPassword(result.tempPassword);
      loadUsers(); // Refresh listen
    } catch (err: any) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const resetCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({ Email: '', Name: '', Phone: '', CVR: '', CompanyName: '' });
    setCreateError('');
    setCreatedPassword('');
  };

  // Kvota farve baseret paa forbrug
  const getQuotaColor = (used: number, limit: number): string => {
    const percentage = (used / limit) * 100;
    if (percentage >= 80) return '#dc2626'; // Roed
    if (percentage >= 50) return '#ca8a04'; // Gul
    return '#16a34a'; // Groen
  };

  // Tier badge style
  const getTierStyle = (tier: string) => {
    const config = TIER_CONFIG[tier] || TIER_CONFIG.Basis;
    return {
      color: config.color,
      backgroundColor: config.bgColor,
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
    };
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'I dag';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'I gaar';
    }
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  };

  return (
    <div style={styles.container}>
      {/* Header med soegning */}
      <div style={styles.header}>
        <h2 style={styles.title}>Brugere</h2>
        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Soeg email, navn, firma, telefon..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={styles.clearButton}
            >
              X
            </button>
          )}
        </div>
      </div>

      {/* Opret bruger knap */}
      <button
        onClick={() => setShowCreateModal(true)}
        style={{
          padding: '10px 20px',
          backgroundColor: '#16a34a',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        + Opret bruger
      </button>

      {/* Error */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Loading */}
      {loading && <div style={styles.loading}>Henter brugere...</div>}

      {/* Empty state */}
      {!loading && users.length === 0 && (
        <div style={styles.empty}>
          {searchQuery ? 'Ingen brugere matcher soogningen' : 'Ingen brugere fundet'}
        </div>
      )}

      {/* User Table */}
      {!loading && users.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Navn</th>
              <th style={styles.th}>Firma</th>
              <th style={styles.th}>Tier</th>
              <th style={styles.th}>Kvota</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Sidst aktiv</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const used = user.CurrentMonthUsage || 0;
              const limit = user.MonthlyLimit || 15;
              const quotaColor = getQuotaColor(used, limit);

              return (
                <tr
                  key={user.UserId}
                  style={styles.tr}
                  onClick={() => onSelectUser(user.UserId)}
                >
                  <td style={styles.td}>
                    <span style={styles.email}>{user.Email}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.name}>{user.Name || '-'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.company}>{user.CompanyName || '-'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={getTierStyle(user.SubscriptionTier)}>
                      {user.SubscriptionTier}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: quotaColor, fontWeight: 600 }}>
                      {used}/{limit}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {user.IsActive ? (
                      <span style={styles.statusActive} title="Aktiv">
                        &#10003;
                      </span>
                    ) : (
                      <span style={styles.statusInactive} title="Inaktiv">
                        &#10005;
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.date}>{formatDate(user.LastLogin)}</span>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectUser(user.UserId);
                      }}
                      style={styles.viewButton}
                    >
                      Se
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Resultat taeller */}
      {!loading && users.length > 0 && (
        <div style={styles.resultCount}>
          Viser {users.length} bruger{users.length !== 1 ? 'e' : ''}
          {searchQuery && ` for "${searchQuery}"`}
        </div>
      )}

      {/* Opret bruger modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '400px',
            maxWidth: '90%',
          }}>
            {createdPassword ? (
              // Success view
              <>
                <h2 style={{ margin: '0 0 20px 0', color: '#16a34a' }}>✓ Bruger oprettet!</h2>
                <p style={{ marginBottom: '10px' }}>Password til brugeren:</p>
                <div style={{
                  backgroundColor: '#f3f4f6',
                  padding: '15px',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: '20px',
                }}>
                  {createdPassword}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(createdPassword)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                  }}
                >
                  Kopier password
                </button>
                <button
                  onClick={resetCreateModal}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Luk
                </button>
              </>
            ) : (
              // Form view
              <>
                <h2 style={{ margin: '0 0 20px 0' }}>Opret ny bruger</h2>

                {createError && (
                  <div style={{
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    padding: '10px',
                    borderRadius: '6px',
                    marginBottom: '15px'
                  }}>
                    {createError}
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={createForm.Email}
                    onChange={(e) => setCreateForm({ ...createForm, Email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Navn *
                  </label>
                  <input
                    type="text"
                    value={createForm.Name}
                    onChange={(e) => setCreateForm({ ...createForm, Name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Telefon</label>
                  <input
                    type="tel"
                    value={createForm.Phone}
                    onChange={(e) => setCreateForm({ ...createForm, Phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Firmanavn</label>
                  <input
                    type="text"
                    value={createForm.CompanyName}
                    onChange={(e) => setCreateForm({ ...createForm, CompanyName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>CVR</label>
                  <input
                    type="text"
                    value={createForm.CVR}
                    onChange={(e) => setCreateForm({ ...createForm, CVR: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleCreateUser}
                    disabled={creating}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      opacity: creating ? 0.7 : 1,
                    }}
                  >
                    {creating ? 'Opretter...' : 'Opret bruger'}
                  </button>
                  <button
                    onClick={resetCreateModal}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    Annuller
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: '#1f2937',
  },
  searchContainer: {
    position: 'relative',
    flex: 1,
    maxWidth: '400px',
  },
  searchInput: {
    width: '100%',
    padding: '12px 40px 12px 16px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  clearButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#9ca3af',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '14px',
    color: '#1f2937',
  },
  email: {
    fontWeight: 500,
    color: '#374151',
  },
  name: {
    color: '#1f2937',
  },
  company: {
    color: '#6b7280',
    fontSize: '13px',
  },
  date: {
    color: '#6b7280',
    fontSize: '13px',
  },
  statusActive: {
    color: '#16a34a',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  statusInactive: {
    color: '#dc2626',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  viewButton: {
    padding: '6px 12px',
    fontSize: '13px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  resultCount: {
    marginTop: '16px',
    fontSize: '13px',
    color: '#9ca3af',
    textAlign: 'center',
  },
};

export default UserList;
